// @ts-strict-ignore
// keep this file isomorphic
import moment from 'moment';

import {Translatable} from 'common/i18n';
import {CALENDAR_DATE_FORMAT, TIMESTAMP_FORMAT, Timezone} from 'common/constants';
import {ScrollingCalendarDateSelection} from 'common/components/scrolling-calendar/util';
import {FEATURE_GATES_UNLIMITED} from 'common/report/constants';
import {TimePeriodSettings} from 'common/report/stores/types';
import {
  assertIsStandardTimeUnit,
  combineDateWithTimeFromDate,
  DEFAULT_TIME_PERIOD_SETTINGS,
  formatDate,
  getLocalTimezone,
  getTimeString,
  parseDate,
  parseDateWithTimeZone,
  parseTimestamp,
} from 'common/util/date';
import {pluralize} from 'common/util/string';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {
  END_OF_TODAY,
  getPresetDateRangeFromAttributes,
  PRESET_DATE_RANGE_DISPLAY_NAMES,
  START_OF_TODAY,
} from 'common/widgets/date-range-picker/models/presets';
import {
  DateRangeAttributes,
  DateRangeType,
  RelativeDate,
  TimeUnit,
} from 'common/widgets/date-range-picker/models/types';

import {LAST_HOURS_MAX, LAST_MINUTES_MAX} from './constants';

export enum AdvancedMenuItem {
  EnableDisableTimePicker = `ENABLE_DISABLE_TIME_PICKER`,
}

// DatePickerMode is not supposed to map 1-1 with DateRangeType, it is purely for UI presentation
export enum DatePickerMode {
  InTheLast = `in the last`,
  Between = `between`,
  Since = `since`,
  On = `on`,
  RelativeFuture = `in the next`,
}

export function getDatePickerMode(dateRange: DateRange): DatePickerMode {
  if (dateRange.isRolling()) {
    return DatePickerMode.InTheLast;
  }

  switch (dateRange.type) {
    case DateRangeType.RelativeBefore:
    case DateRangeType.RelativeAfter:
    case DateRangeType.NotRelativeAfter: {
      return DatePickerMode.InTheLast;
    }
    case DateRangeType.RelativeFuture: {
      return DatePickerMode.RelativeFuture;
    }
    case DateRangeType.Before:
    case DateRangeType.On:
    case DateRangeType.NotOn: {
      return DatePickerMode.On;
    }
    case DateRangeType.NotBetween:
    case DateRangeType.Between: {
      return DatePickerMode.Between;
    }
    case DateRangeType.Since: {
      return DatePickerMode.Since;
    }

    default:
      throw new Error(`"date-range" attribute contains DateRange info with unsupported DateRangeType`);
  }
}

export function getCalendarSelection(
  dateRange: DateRange,
  asOfDate: moment.Moment,
  timezone?: Timezone,
): ScrollingCalendarDateSelection {
  const {from, to} = dateRange.getCalendarDatesAsOf(asOfDate.format(CALENDAR_DATE_FORMAT));
  let startDate = moment(parseDate(from));
  let endDate = moment(parseDate(to));

  if (timezone) {
    startDate = parseDateWithTimeZone(from, timezone);
    endDate = parseDateWithTimeZone(to, timezone);
  }

  // clamp endDate to startDate
  if (startDate.isSameOrAfter(endDate)) {
    endDate = moment(parseDate(from));
    if (timezone) {
      endDate = parseDateWithTimeZone(from, timezone);
    }
  }

  const offsetFrom = timezone ? asOfDate.format(`YYYY-MM-DD`) : asOfDate.toISOString();

  if (startDate.isSameOrBefore(endDate, `day`)) {
    const showOffset = dateRange.isRolling() && endDate.isBefore(asOfDate, `day`);
    const isRelativeFutureMode = getDatePickerMode(dateRange) === DatePickerMode.RelativeFuture;

    return {
      start: timezone ? startDate.format(`YYYY-MM-DD`) : startDate.toISOString(),
      end: timezone ? endDate.format(`YYYY-MM-DD`) : endDate.toISOString(),
      highlightStart: !isRelativeFutureMode,
      highlightEnd:
        isRelativeFutureMode ||
        (!endDate.isSame(startDate, `day`) && getDatePickerMode(dateRange) === DatePickerMode.Between),
      ...(showOffset ? {offsetFrom} : {}),
    };
  }

  return null;
}

export function getRelativeWindowUnit(dateRange: DateRange): TimeUnit {
  if (dateRange.window) {
    return dateRange.window.unit;
  }
  if (dateRange.isRolling()) {
    const relativeTo = dateRange.to as RelativeDate;
    if (relativeTo.unit) {
      return relativeTo.unit;
    }
  }
  return TimeUnit.Day;
}

export function getRelativeWindowValue(dateRange: DateRange, asOfDate: moment.Moment): number {
  if (dateRange.window) {
    return dateRange.window.value;
  } else if (
    (dateRange.from as RelativeDate)?.unit &&
    (dateRange.from as RelativeDate).unit === (dateRange.to as RelativeDate).unit
  ) {
    // Subtract the `to` offset from the `from` window value since the unit is the same.
    // This is more reliable than converting the date back and forth multiple times in the block below, since it may not account for the correct
    // start/end of unit alignment across all unit combinations.
    return (dateRange.from as RelativeDate)?.value - (dateRange.to as RelativeDate)?.value;
  }

  const {from, to} = dateRange.getCalendarDatesAsOf(asOfDate.format(`YYYY-MM-DD`));
  const startDate = moment(parseDate(from));
  const endDate = moment(parseDate(to));
  const windowUnit = getRelativeWindowUnit(dateRange);
  assertIsStandardTimeUnit(windowUnit);
  return endDate.diff(startDate, windowUnit);
}

// returns offset if there is a valid offset
export function getRelativeOffset(dateRange: DateRange, asOfDate: moment.Moment): null | RelativeDate {
  if (dateRange.window?.unit === TimeUnit.Day) {
    return {unit: TimeUnit.Day, value: 0};
  }
  if (dateRange.isRolling()) {
    if (typeof dateRange.to === `object`) {
      const relativeTo = dateRange.to;
      return relativeTo;
    } else if (typeof dateRange.to === `string`) {
      const {to} = dateRange.getCalendarDatesAsOf(asOfDate.format(CALENDAR_DATE_FORMAT));
      const endDate = moment(parseDate(to));
      const value = asOfDate.diff(endDate, TimeUnit.Day);
      return {unit: TimeUnit.Day, value};
    }
  }
  return null;
}

// ------- note: always output DateRange attributes instead of DateRange class instances because the appropriate class is determined by display context, which is a UI concern -------

/** get date range attributes for "in the last" or "in the next" types */
export function getRelativeDateRangeAttributes(
  window: RelativeDate,
  offset: null | RelativeDate,
  datePickerMode: DatePickerMode.InTheLast | DatePickerMode.RelativeFuture,
): DateRangeAttributes {
  if (offset) {
    if (window.unit !== offset.unit) {
      throw new Error(`window and offset units must match`);
    }
    if (offset.unit !== TimeUnit.Day) {
      throw new Error(`offset unit must be Day`);
    }

    if (offset.value > 0) {
      const isSingleDay = window.value === 0 && window.unit === TimeUnit.Day;
      return {
        type: isSingleDay ? DateRangeType.On : DateRangeType.Between,
        to: offset,
        from: {
          unit: window.unit,
          value: offset.value + window.value,
        },
        rolling: true,
      };
    }
  }

  return {
    type: datePickerMode === DatePickerMode.RelativeFuture ? DateRangeType.RelativeFuture : DateRangeType.RelativeAfter,
    window,
  };
}

export function getBetweenDateRangeAttributes(
  fromDate: moment.Moment,
  toDate: moment.Moment,
  times?: {fromTime: moment.Moment; toTime: moment.Moment},
): DateRangeAttributes {
  const firstDate = fromDate.isSameOrBefore(toDate, `day`) || !toDate.isValid() ? fromDate : toDate;
  const secondDate = fromDate.isSameOrAfter(toDate, `day`) ? fromDate : toDate;

  if (times?.fromTime && times?.fromTime.isValid() && times?.toTime && times?.toTime.isValid()) {
    // When we pass times keep the times attached to their original positions (from/to), regardless
    // of whether we switch the dates.
    return {
      type: DateRangeType.Between,
      from: combineDateWithTimeFromDate(firstDate, times.fromTime).format(TIMESTAMP_FORMAT),
      to: combineDateWithTimeFromDate(secondDate, times.toTime).format(TIMESTAMP_FORMAT),
    };
  }
  return {
    type: DateRangeType.Between,
    from: firstDate.format(TIMESTAMP_FORMAT),
    to: secondDate.format(TIMESTAMP_FORMAT),
  };
}

export function getOnDateRangeAttributes(onDate: moment.Moment): DateRangeAttributes {
  return {
    type: DateRangeType.On,
    from: onDate.format(TIMESTAMP_FORMAT),
    to: onDate.format(TIMESTAMP_FORMAT),
  };
}

export function getSinceDateRangeAttributes(sinceDate: moment.Moment): DateRangeAttributes {
  return {
    type: DateRangeType.Since,
    from: sinceDate.format(TIMESTAMP_FORMAT),
  };
}

export function getSimilarDateRange(
  dateRange: DateRange,
  asOfDate: moment.Moment,
  type: DateRangeType,
  timezone?: Timezone,
  times?: {fromTime: moment.Moment; toTime: moment.Moment},
): DateRangeAttributes | null {
  const {from, to} = dateRange.getCalendarDatesAsOf(asOfDate.format(TIMESTAMP_FORMAT));
  let fromDate = moment(parseDate(from));
  let toDate = moment(parseDate(to));

  if (timezone) {
    fromDate = parseDateWithTimeZone(from, timezone);
    toDate = parseDateWithTimeZone(to, timezone);
  }

  switch (type) {
    case DateRangeType.RelativeBefore:
    case DateRangeType.RelativeAfter:
    case DateRangeType.NotRelativeAfter:
    case DateRangeType.RelativeFuture: {
      const windowValue = getRelativeWindowValue(dateRange, asOfDate);
      const windowUnit = getRelativeWindowUnit(dateRange);
      const offsetDays = asOfDate.diff(toDate, TimeUnit.Day);
      const canBeRolling = dateRange.canUseRolling() && windowUnit === TimeUnit.Day && offsetDays > 0;

      if (canBeRolling) {
        const isSingleDay = windowValue === 0 && windowUnit === TimeUnit.Day;
        return {
          type: isSingleDay ? DateRangeType.On : DateRangeType.Between,
          to: {
            unit: windowUnit,
            value: offsetDays,
          },
          from: {
            unit: windowUnit,
            value: offsetDays + windowValue,
          },
          rolling: true,
        };
      } else if (windowValue === 0) {
        return {
          from: START_OF_TODAY,
          to: END_OF_TODAY,
          type: DateRangeType.Between,
        };
      }
      return {
        type,
        window: {unit: windowUnit, value: windowValue},
      };
    }
    case DateRangeType.Between:
    case DateRangeType.NotBetween: {
      return {
        ...getBetweenDateRangeAttributes(fromDate, toDate, times),
        type,
      };
    }
    case DateRangeType.On:
    case DateRangeType.NotOn: {
      return {
        from: getBetweenDateRangeAttributes(fromDate, toDate, times).from,
        to: getBetweenDateRangeAttributes(fromDate, toDate, times).from,
        type,
      };
    }
    case DateRangeType.Since: {
      return getSinceDateRangeAttributes(toDate.isBefore(fromDate, `day`) ? toDate : fromDate);
    }
    case DateRangeType.Before: {
      return {
        type,
        to: getSinceDateRangeAttributes(fromDate).from,
      };
    }
  }
  return null;
}

export function getSimilarDateRangeForPickerMode(
  dateRange: DateRange,
  asOfDate: moment.Moment,
  pickerMode: DatePickerMode,
  timezone?: Timezone,
  times?: {fromTime: moment.Moment; toTime: moment.Moment},
): DateRangeAttributes | null {
  const defaultDateRangeType: DateRangeType = (() => {
    switch (pickerMode) {
      case DatePickerMode.InTheLast:
        return DateRangeType.RelativeAfter;
      case DatePickerMode.RelativeFuture:
        return DateRangeType.RelativeFuture;
      case DatePickerMode.On:
        return DateRangeType.On;
      case DatePickerMode.Between:
        return DateRangeType.Between;
      case DatePickerMode.Since:
        return DateRangeType.Since;
      default: {
        const unreachable: never = pickerMode; // a trick to enforce full enum coverage in switch case
        throw new Error(`unknown picker mode ${unreachable}`);
      }
    }
  })();
  return getSimilarDateRange(dateRange, asOfDate, defaultDateRangeType, timezone, times);
}

export function isMaxHistoryFinite(maxHistoryDays: number): boolean {
  if (!Number.isInteger(maxHistoryDays)) {
    // note: Number.isInteger() also checks that the value is finite
    return false;
  }
  if (maxHistoryDays <= 0) {
    return false;
  }
  return maxHistoryDays !== FEATURE_GATES_UNLIMITED;
}

export function generateCommonDateRangeTrackingProps(
  dateRange: DateRange | undefined,
  projectAsOfDate: string | null,
  projectTimezone: Timezone | undefined,
  timePeriodSettings: TimePeriodSettings | undefined,
) {
  if (!dateRange) {
    return {
      'Date range from': null,
      'Date range to': null,
      'Date window duration (in days)': null,
      'Date window units selection': null,
    };
  }

  const {from, to} = dateRange.getCalendarDatesAsOf(
    projectAsOfDate,
    CALENDAR_DATE_FORMAT,
    projectTimezone,
    timePeriodSettings,
  );
  const fromDate = moment(parseDate(from));
  const toDate = moment(parseDate(to));
  const dateRangePickerMode = getDatePickerMode(dateRange);
  return {
    'Date picker mode': getDatePickerMode(dateRange),
    'Date range from': fromDate.toISOString(),
    'Date range to': toDate.toISOString(),
    'Date window duration (in days)': toDate.diff(fromDate, `day`),
    'Date window units selection': [DatePickerMode.InTheLast, DatePickerMode.RelativeFuture].includes(
      dateRangePickerMode,
    )
      ? getRelativeWindowUnit(dateRange)
      : null,
    'Date window value': dateRange.window?.value ?? (dateRange.from as RelativeDate)?.value ?? null,
    'Date window offset value': (dateRange.to as RelativeDate)?.value ?? null,
    'Is rolling': dateRange.rolling ?? false,
    'Future from date': dateRange.hasFutureFromDate(
      projectTimezone ?? getLocalTimezone(),
      projectAsOfDate,
      timePeriodSettings ?? DEFAULT_TIME_PERIOD_SETTINGS,
    ),
    'Future to date': dateRange.hasFutureToDate(
      projectTimezone ?? getLocalTimezone(),
      projectAsOfDate,
      timePeriodSettings ?? DEFAULT_TIME_PERIOD_SETTINGS,
    ),
  };
}

export function generateDatePickerTrackingProps(
  dateRange: DateRange,
  reportName: string,
  pickerMode: DatePickerMode,
  projectAsOfDate: moment.Moment,
  trackedUseCase: string,
  projectTimezone: Timezone | undefined,
  timePeriodSettings: TimePeriodSettings | undefined,
): any {
  const commonTrackingProps = generateCommonDateRangeTrackingProps(
    dateRange,
    projectAsOfDate.format(CALENDAR_DATE_FORMAT),
    projectTimezone,
    timePeriodSettings,
  );
  return {
    ...commonTrackingProps,
    'Date picker mode': commonTrackingProps[`Date picker mode`] ?? pickerMode,
    tab: reportName,
    'usage context': trackedUseCase,
  };
}

type DateRangeLabelFormatMode = `simple` | `sentence` | `none` | `numeric`;

// TODO go/j/AE-2063 clean up `now` and `projectAsOfDate` params, using getCurrentMomentAsOf
// instead.
export function getDateRangeLabel({
  dateRange,
  matchingPresetLabel,
  projectAsOfDate,
  now,
  formatMode = `simple`,
  asString = false,
}: {
  dateRange: null | DateRange;
  matchingPresetLabel: null | Translatable;
  projectAsOfDate: null | string;
  now: moment.Moment;
  formatMode?: DateRangeLabelFormatMode;
  asString?: boolean;
}): Translatable {
  if (!dateRange) {
    return `Select Date`;
  }
  const parseAndFormatDate =
    (val: string): Translatable =>
    (i18n) =>
      i18n.formatDate(
        parseDate(val, {iso: true}),
        formatMode === `numeric`
          ? {
              month: `numeric`,
              day: `numeric`,
              year: `2-digit`,
            }
          : {
              month: `short`,
              day: `numeric`,
              year: `numeric`,
            },
      );

  const explicitAsOfDate = parseDate(projectAsOfDate);
  const asOfDate = explicitAsOfDate ? moment(explicitAsOfDate) : now;

  let rangeLabel: Translatable = `unknown`;
  let asOfLabel: string = explicitAsOfDate ? ` (as of ${formatDate(explicitAsOfDate)})` : ``;

  // In the last presets are treated like normal date ranges for the purposes of formatting options.
  // We ignore the `matchingPresetLabel` for these.
  const pickerMode = getDatePickerMode(dateRange);
  if (!matchingPresetLabel && pickerMode !== DatePickerMode.InTheLast) {
    // If no matching preset label was specified by the caller, try to get the matching preset via
    // the DateRangeAttributes
    const presetDateRange = getPresetDateRangeFromAttributes(dateRange);
    matchingPresetLabel = presetDateRange ? PRESET_DATE_RANGE_DISPLAY_NAMES[presetDateRange] : null;
  }
  if (matchingPresetLabel && pickerMode !== DatePickerMode.InTheLast) {
    if (asString) {
      return () => `${matchingPresetLabel}`;
    }
    return ({print}) => `${print(matchingPresetLabel)}`;
  }

  // TODO(i18n): translate the strings in this block
  switch (pickerMode) {
    case DatePickerMode.On: {
      const {to} = dateRange.getCalendarDatesAsOf(projectAsOfDate, TIMESTAMP_FORMAT);
      let toTimeText = ``;
      if (dateRange.hasSubDayGranularity()) {
        toTimeText = ` ${getTimeString(parseTimestamp(to))}`;
      }
      rangeLabel = ({print}) => `${print(parseAndFormatDate(to))}${toTimeText}`;
      asOfLabel = ``;
      break;
    }
    case DatePickerMode.Between: {
      const {from, to} = dateRange.getCalendarDatesAsOf(projectAsOfDate, TIMESTAMP_FORMAT);
      let [fromTimeText, toTimeText] = [``, ``];
      if (dateRange.hasSubDayGranularity()) {
        [fromTimeText, toTimeText] = [
          ` ${getTimeString(parseTimestamp(from))}`,
          ` ${getTimeString(parseTimestamp(to))}`,
        ];
      }
      const internalPrefix = formatMode === `sentence` ? `Between ` : ``;
      const dateRangeUnit = dateRange.hasSubDayGranularity() ? `minute` : `day`;
      if (moment(from).isSame(moment(to), dateRangeUnit)) {
        rangeLabel = ({print}) => `${internalPrefix}${print(parseAndFormatDate(from))}${fromTimeText}`;
      } else {
        rangeLabel = ({print}) =>
          `${internalPrefix}${print(parseAndFormatDate(from))}${fromTimeText} — ${print(
            parseAndFormatDate(to),
          )}${toTimeText}`;
      }
      asOfLabel = ``;
      break;
    }
    case DatePickerMode.Since: {
      const {from} = dateRange.getCalendarDatesAsOf(projectAsOfDate, TIMESTAMP_FORMAT);
      const internalPrefix = formatMode === `simple` || formatMode === `sentence` ? `Since ` : ``;
      let fromTimeText = ``;
      if (dateRange.hasSubDayGranularity()) {
        fromTimeText = ` ${getTimeString(parseTimestamp(from))}`;
      }
      rangeLabel = ({print}) => `${internalPrefix}${print(parseAndFormatDate(from))}${fromTimeText}`;
      if (explicitAsOfDate) {
        asOfLabel = ` (until ${formatDate(explicitAsOfDate)})`;
      }
      break;
    }
    case DatePickerMode.InTheLast: {
      const internalPrefix = formatMode === `simple` ? `Last ` : formatMode === `sentence` ? `In the last ` : ``;

      if (dateRange.type === DateRangeType.Since && dateRange.isRolling()) {
        // handle since rolling dates (unbounded dates)
        const {unit: fromUnit, value: fromValue} = dateRange.from as RelativeDate;
        rangeLabel = `Last ${fromValue} ${pluralize(fromUnit, fromValue)}`;
      } else {
        const offset = getRelativeOffset(dateRange, asOfDate);
        const windowValue = getRelativeWindowValue(dateRange, asOfDate);
        const windowUnit = getRelativeWindowUnit(dateRange);
        rangeLabel = `${internalPrefix}${windowValue} ${pluralize(windowUnit, windowValue)}`;
        if (offset?.value > 0) {
          rangeLabel = `${rangeLabel} ending ${offset.value} ${pluralize(offset.unit, offset.value)} ago`;
        } else if (offset?.value < 0) {
          rangeLabel = `next ${Math.abs(offset.value)} ${pluralize(offset.unit, Math.abs(offset.value))}`;
        }
      }
      break;
    }
    case DatePickerMode.RelativeFuture: {
      const windowValue = getRelativeWindowValue(dateRange, asOfDate);
      const windowUnit = getRelativeWindowUnit(dateRange);
      rangeLabel = `${windowValue} ${pluralize(windowUnit, windowValue)}`;
      break;
    }
    default: {
      const unreachable: never = pickerMode; // a trick to enforce full enum coverage in switch case
      throw new Error(`unknown picker mode ${unreachable}`);
    }
  }

  return ({print}) => `${print(rangeLabel)}${asOfLabel}`;
}

export function getRelativeInputMax(unit: TimeUnit): number | null {
  if (unit === TimeUnit.Hour) {
    return LAST_HOURS_MAX;
  }
  if (unit === TimeUnit.Minute) {
    return LAST_MINUTES_MAX;
  }
  return null;
}

export function getRelativeInputErrorMessage(
  value: number | null | undefined,
  unit: TimeUnit,
  shouldEnforceMax: boolean,
): Translatable {
  if (Number.isNaN(value) || (value && !Number.isInteger(value))) {
    return ({t}) => t(`reports.date-range.invalid.last-non-integer-input`);
  }
  if (value < 0) {
    return ({t}) =>
      t(`reports.date-range.invalid.last-range-min`, {
        min: 0,
        unit,
      });
  }

  if (shouldEnforceMax) {
    const max: number = getRelativeInputMax(unit);
    if (max && value > max) {
      return ({t}) =>
        t(`reports.date-range.invalid.last-range-max`, {
          max,
          unit,
        });
    }
  }
  return ``;
}
